// Vue
import { Component, Watch, Vue } from "vue-property-decorator";
import { Observer } from "mobx-vue";
// Template
import template from "./template.vue";
// Base Module
import ModuleBase from "#/common/ModuleBase";
// Components
import ModuleWrapper from "#/components/module_wrapper/ModuleWrapper";

import { Api } from "#/api";
import { KatalogData } from "#/common/models/katalog_data/KatalogData";
import { StueckzahlInput } from "./stueckzahl_input/StueckzahlInput";
import { TableColumnOptions } from "@mgs/ci2019-common/src/interfaces/TableColumnOptions";

@Observer
@Component({
  mixins: [template],
  components: {
    ModuleWrapper,
    StueckzahlInput,
  },
})
export class KatalogTabelleModule extends ModuleBase {
  public getBackgroundColorFromInstance(instance: object) {
    return "white";
  }

  private data: KatalogData | null = null;
  private filter: object = {};
  private session: object = {};
  private isLoading: boolean = true;

  @Watch("filter", { deep: true })
  onFilterChange() {
    this.loadData();
  }

  get selectFields() {
    if (this.data) {
      return this.data.selects;
    } else {
      return {};
    }
  }

  get totalCountText() {
    if (this.data) {
      return this.data.config.countText;
    } else {
      return "";
    }
  }

  get selectFieldColumns() {
    if (this.data) {
      return 12 / this.data.config.select_columns;
    } else {
      return 3;
    }
  }

  get matrixMaxColumns() {
    let defaultColumns = 5;
    if (this.data && this.data.config.matrix_max_columns > 0) {
      return this.data.config.matrix_max_columns;
    } else {
      return defaultColumns;
    }
  }

  get linkTarget() {
    let defaultTarget = "_self";
    if (this.data && this.data.config.link_target) {
      return this.data.config.link_target;
    } else {
      return defaultTarget;
    }
  }

  get aggregationIndexMapping() {
    if (this.data && this.data.config.aggregated_columns) {
      let mapping = {};
      this.data.config.aggregated_columns.forEach((value, index) => {
        let elements = value.split("-");
        let begin = parseInt(elements[0], 10);
        let end = parseInt(elements[1], 10);

        if (begin && end && begin < end) {
          for (let i = begin; i <= end; i++) {
            mapping[i - 1] = begin - 1 - index;
          }
        }
      });

      return mapping;
    } else {
      return {};
    }
  }

  get aggregationKeyMapping(): { [key: string]: string } {
    if (this.data) {
      let mapping = this.aggregationIndexMapping;

      let aggregatedColumns: { keys: string[] }[] = [];
      Object.entries(this.data.config.columns).forEach(
        ([key, value], index) => {
          if (mapping[index] && aggregatedColumns[mapping[index]]) {
            aggregatedColumns[mapping[index]].keys.push(value.key);
          } else {
            aggregatedColumns.push({
              keys: [key],
            });
          }
        }
      );

      let keyMapping = {};
      aggregatedColumns.forEach((entry) => {
        entry.keys.forEach((key) => {
          keyMapping[key] = entry.keys.join("__");
        });
      });

      return keyMapping;
    } else {
      return {};
    }
  }

  get tableColumns() {
    if (this.data) {
      let result: { [key: string]: TableColumnOptions } = {};

      Object.entries(this.aggregationKeyMapping).forEach(
        ([baseKey, targetKey]) => {
          let name = this.data!.config.columns[baseKey].name;
          let width = this.data!.config.columns[baseKey].width;

          if (result[targetKey]) {
            result[targetKey].name += "<br>" + name;
          } else {
            result[targetKey] = {
              name: name,
              sortable: false,
            };
          }

          if (width && width > 0) {
            result[targetKey].width = width;
          }
        }
      );

      return result;
    } else {
      return {};
    }
  }

  get tableData() {
    if (this.data) {
      let keyMapping = this.aggregationKeyMapping;

      let result: any[] = [];
      this.data.data.forEach((entry) => {
        let row = {};
        Object.entries(this.data!.config.columns).forEach(([key, value]) => {
          let targetKey = keyMapping[key];
          if (row[targetKey]) {
            row[targetKey] += "<br>" + entry[key];
          } else {
            row[targetKey] = entry[key];
          }
        });

        result.push(row);
      });

      return result;
    } else {
      return [];
    }
  }

  getImageBasePath(columnKey) {
    if (
      this.data &&
      this.data.config &&
      this.data.config.columns[columnKey] &&
      this.data.config.columns[columnKey]["base_path"]
    ) {
      return this.data.config.columns["IMAGENAME"]["base_path"];
    }

    return "";
  }

  public created() {
    this.initialize();
  }

  public async initialize() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("token")) {
      let token = urlParams.get("token")!;

      this.isLoading = true;
      this.session = await Api.katalogService.getSession(token);
      this.data = await Api.katalogService.getData(
        this.session["klasse"],
        this.instance["katalog"],
        this.session["completeFilter"] || {},
        {}
      );
      this.isLoading = false;
    } else {
      this.isLoading = true;
      this.data = await Api.katalogService.getData(
        null,
        this.instance["katalog"],
        {},
        {}
      );
      this.isLoading = false;
    }

    // Wenn es ein Teilekatalog ist, auch noch den aktuellen Warenkorb laden
    if (this.data.config.columns["ajax_stueckzahl"]) {
      this.$store.warenkorb.getCurrent();
    }
  }

  public async loadData() {
    this.isLoading = true;
    this.data = await Api.katalogService.getData(
      this.session["klasse"],
      this.instance["katalog"],
      this.session["completeFilter"] || {},
      this.filter
    );
    this.isLoading = false;

    // Wenn es ein Teilekatalog ist, auch noch den aktuellen Warenkorb laden
    if (this.data.config.columns["ajax_stueckzahl"]) {
      this.$store.warenkorb.getCurrent();
    }
  }

  getPlaceholderForKey(key) {
    if (
      this.data &&
      this.data.config.preselect &&
      this.data.config.preselect[key]
    ) {
      return this.data.config.preselect[key];
    }

    return "";
  }

  onResetFilter(key) {
    if (this.filter[key]) {
      delete this.filter[key];
      this.onFilterChange();
    }
  }

  matrixSpracheRows(matrix_sprache) {
    if (this.data && matrix_sprache) {
      let result: any[][] = [];

      Object.entries(matrix_sprache).forEach(
        ([sprachKey, sprachData], index) => {
          let insertIndex = Math.floor(index / this.matrixMaxColumns);

          if (result.length - 1 < insertIndex) {
            result.push([]);
          }

          result[insertIndex].push({ key: sprachKey, data: sprachData });
        }
      );

      if (result[result.length - 1].length < this.matrixMaxColumns) {
        for (
          let i = result[result.length - 1].length;
          i < this.matrixMaxColumns;
          i++
        ) {
          result[result.length - 1].push(null);
        }
      }

      return result;
    } else {
      return [];
    }
  }
}
